import { useMemo, useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'

import type { DeliveryTripStatus } from '@/lib/api/delivery-trip-api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { useDeliveryTrips } from '@/hooks/use-delivery-trips-query'
import { useAuth } from '@/hooks/use-auth'
import { useDashboardRole } from '@/components/dashboard/use-dashboard-role'
import { driverTripScope } from '@/lib/delivery-trip-scope'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/_sidebar/delivery-trips/')({
    component: DeliveryTripsPage,
})

const STATUS_OPTIONS: Array<DeliveryTripStatus | 'all'> = [
    'all',
    'active',
    'closed',
]

const statusLabel = (s: DeliveryTripStatus) =>
    s === 'active' ? m.trip_status_active() : m.trip_status_closed()

function DeliveryTripsPage() {
    const [page, setPage] = useState(1)
    const [statusFilter, setStatusFilter] = useState<
        DeliveryTripStatus | 'all'
    >('all')
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')

    // A driver only sees their own trips; admin/manager see all. Scope is
    // applied via the driver_id filter (UI-level — see driverTripScope).
    const { user } = useAuth()
    const dashboardRole = useDashboardRole({ allowKioskRedirect: false })
    const isDriver =
        dashboardRole.kind === 'dashboard' &&
        dashboardRole.dashboard === 'driver'
    const driverId = driverTripScope(isDriver, user?.id)

    const params = useMemo(
        () => ({
            page,
            per_page: 20,
            status: statusFilter === 'all' ? undefined : statusFilter,
            from: from || undefined,
            to: to || undefined,
            driver_id: driverId,
        }),
        [page, statusFilter, from, to, driverId],
    )

    const { data, isLoading } = useDeliveryTrips(params)
    const trips = data?.data ?? []
    const meta = data?.meta

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <CardTitle>{m.trip_history_title()}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex flex-col sm:flex-row gap-2">
                        <Select
                            value={statusFilter}
                            onValueChange={(v) => {
                                setStatusFilter(v as DeliveryTripStatus | 'all')
                                setPage(1)
                            }}
                        >
                            <SelectTrigger className="sm:w-40">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {STATUS_OPTIONS.map((s) => (
                                    <SelectItem key={s} value={s}>
                                        {s === 'all'
                                            ? m.vehicle_status_all()
                                            : statusLabel(s)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Input
                            type="date"
                            value={from}
                            onChange={(e) => {
                                setFrom(e.target.value)
                                setPage(1)
                            }}
                            className="sm:w-44"
                        />
                        <Input
                            type="date"
                            value={to}
                            onChange={(e) => {
                                setTo(e.target.value)
                                setPage(1)
                            }}
                            className="sm:w-44"
                        />
                    </div>

                    {isLoading ? (
                        <p className="text-sm text-muted-foreground">
                            {m.common_loading()}
                        </p>
                    ) : trips.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-8 text-center">
                            {m.trip_empty()}
                        </p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{m.trip_code()}</TableHead>
                                    <TableHead>{m.trip_driver()}</TableHead>
                                    <TableHead>{m.trip_vehicle()}</TableHead>
                                    <TableHead>
                                        {m.common_status_label()}
                                    </TableHead>
                                    <TableHead>{m.trip_started_at()}</TableHead>
                                    <TableHead>{m.trip_ended_at()}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {trips.map((t) => (
                                    <TableRow key={t.id}>
                                        <TableCell className="font-medium">
                                            <Link
                                                to="/delivery-trips/$id"
                                                params={{ id: t.id }}
                                                className="text-primary hover:underline"
                                            >
                                                {t.trip_code}
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            {t.driver_name ?? '—'}
                                        </TableCell>
                                        <TableCell>
                                            {t.vehicle
                                                ? `${t.vehicle.plate_number} — ${t.vehicle.name}`
                                                : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant={
                                                    t.status === 'active'
                                                        ? 'success'
                                                        : 'secondary'
                                                }
                                            >
                                                {statusLabel(t.status)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {new Date(
                                                t.started_at,
                                            ).toLocaleString()}
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {t.ended_at
                                                ? new Date(
                                                      t.ended_at,
                                                  ).toLocaleString()
                                                : m.trip_running_now()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}

                    {meta && meta.total_pages > 1 && (
                        <div className="flex items-center justify-end gap-2 pt-2">
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => p - 1)}
                            >
                                {m.common_prev()}
                            </Button>
                            <span className="text-sm text-muted-foreground">
                                {page} / {meta.total_pages}
                            </span>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page >= meta.total_pages}
                                onClick={() => setPage((p) => p + 1)}
                            >
                                {m.common_next()}
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
